// Session Email Notification Service
// Handles sending email notifications for session events

import { logDebug, logInfo, logWarn, logError } from '@/utils/secureLogger';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://elevated-backend-xyaskmbwha-uc.a.run.app/api';

interface SessionApprovedData {
  studentEmail: string;
  studentName: string;
  tutorName: string;
  subject: string;
  sessionDate: string;
  sessionTime: string;
  sessionMode?: 'online' | 'face-to-face';
  location?: string;
  meetingLink?: string;
}

interface SessionDeclinedData {
  studentEmail: string;
  studentName: string;
  tutorName: string;
  subject: string;
  sessionDate: string;
  sessionTime: string;
  declineReason?: string;
}

interface SessionBookedData {
  tutorEmail: string;
  tutorName: string;
  studentName: string;
  subject: string;
  sessionDate: string;
  sessionTime: string;
  sessionMode?: 'online' | 'face-to-face';
  location?: string;
  learningGoals?: string;
}

interface SessionCancelledData {
  recipientEmail: string;
  recipientName: string;
  otherPartyName?: string;
  subject: string;
  sessionDate: string;
  sessionTime: string;
  cancellationReason?: string;
  cancelledBy?: 'student' | 'tutor';
}

interface SessionRescheduledData {
  recipientEmail: string;
  recipientName: string;
  otherPartyName?: string;
  subject: string;
  oldSessionDate: string;
  oldSessionTime: string;
  newSessionDate: string;
  newSessionTime: string;
  sessionMode?: 'online' | 'face-to-face';
  location?: string;
  meetingLink?: string;
  rescheduledBy?: 'student' | 'tutor';
  reschedulingReason?: string;
}

/**
 * Send session approved email to student
 */
export const sendSessionApprovedEmail = async (data: SessionApprovedData): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/email/session-approved/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      logInfo('Session approved email sent successfully');
      return true;
    } else {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      logError('Failed to send session approved email', new Error(JSON.stringify(errorData)));
      return false;
    }
  } catch (error) {
    logError('Error sending session approved email', error instanceof Error ? error : new Error(String(error)));
    return false;
  }
};

/**
 * Send session declined email to student
 */
export const sendSessionDeclinedEmail = async (data: SessionDeclinedData): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/email/session-declined/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      logInfo('Session declined email sent successfully');
      return true;
    } else {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      logError('Failed to send session declined email', new Error(JSON.stringify(errorData)));
      return false;
    }
  } catch (error) {
    logError('Error sending session declined email', error instanceof Error ? error : new Error(String(error)));
    return false;
  }
};

/**
 * Send session booked email to tutor
 */
export const sendSessionBookedEmail = async (data: SessionBookedData): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/email/session-booked/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      logInfo('Session booking notification sent successfully to tutor');
      return true;
    } else {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      logError('Failed to send session booking email', new Error(JSON.stringify(errorData)));
      return false;
    }
  } catch (error) {
    logError('Error sending session booking email', error instanceof Error ? error : new Error(String(error)));
    return false;
  }
};

/**
 * Send session cancelled email to recipient
 */
export const sendSessionCancelledEmail = async (data: SessionCancelledData): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/email/session-cancelled/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      logInfo('Session cancelled email sent successfully');
      return true;
    } else {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      logError('Failed to send session cancelled email', new Error(JSON.stringify(errorData)));
      return false;
    }
  } catch (error) {
    logError('Error sending session cancelled email', error instanceof Error ? error : new Error(String(error)));
    return false;
  }
};

/**
 * Send session rescheduled email to recipient
 */
export const sendSessionRescheduledEmail = async (data: SessionRescheduledData): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/email/session-rescheduled/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (response.ok) {
      logInfo('Session rescheduled email sent successfully');
      return true;
    } else {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: errorText };
      }
      logError('Failed to send session rescheduled email', new Error(JSON.stringify(errorData)));
      return false;
    }
  } catch (error) {
    logError('Error sending session rescheduled email', error instanceof Error ? error : new Error(String(error)));
    return false;
  }
};

/**
 * Helper function to format session data for email
 */
export const formatSessionDataForEmail = (session: any, userType: 'student' | 'tutor') => {
  logDebug("Formatting session data for email", { sessionId: session.id });
  
  let startDate, endDate;
  
  // Handle different session data structures with better error handling
  if (session.startAt || session.endAt || session.start || session.end) {
    // Handle sessions with startAt/endAt or start/end (Firestore timestamps or Date objects)
    const startField = session.startAt || session.start;
    const endField = session.endAt || session.end;
    
    if (startField instanceof Date) {
      startDate = startField;
    } else if (startField?.toDate && typeof startField.toDate === 'function') {
      startDate = startField.toDate();
    } else if (startField?.seconds) {
      // Firestore timestamp format
      startDate = new Date(startField.seconds * 1000);
    } else if (startField) {
      startDate = new Date(startField);
    } else {
      logWarn("No valid start time found, using current time");
      startDate = new Date();
    }
    
    // Process endAt - if not available, calculate from startAt + duration
    if (endField instanceof Date) {
      endDate = endField;
    } else if (endField?.toDate && typeof endField.toDate === 'function') {
      endDate = endField.toDate();
    } else if (endField?.seconds) {
      // Firestore timestamp format
      endDate = new Date(endField.seconds * 1000);
    } else if (endField) {
      endDate = new Date(endField);
    } else {
      // Calculate endAt from startAt + 1 hour (default session duration)
      logDebug("No endAt found, calculating from startAt + 1 hour");
      endDate = new Date(startDate.getTime() + (60 * 60 * 1000));
    }
  } else if (session.date && session.time) {
    // Handle sessions with separate date and time strings (PendingSession format)
    logDebug("Using date/time format from session");
    
    try {
      // Parse the date and time strings
      // Support multiple date formats: "2024-10-03", "10/03/2024", "Tue, Dec 2, 2025", etc.
      const dateStr = session.date;
      const timeStr = session.time;
      
      logDebug(`Parsing date: ${dateStr}, time: ${timeStr}`);
      
      // First try direct parsing for common formats
      const dateTimeString = `${dateStr} ${timeStr}`;
      startDate = new Date(dateTimeString);
      
      // If parsing failed, try alternative approaches
      if (isNaN(startDate.getTime())) {
        logWarn("First date parsing failed, trying alternative approaches");
        
        // Handle day name format like "Tue, Dec 2, 2025"
        if (dateStr.includes(',') && (dateStr.includes('Jan') || dateStr.includes('Feb') || dateStr.includes('Mar') || 
            dateStr.includes('Apr') || dateStr.includes('May') || dateStr.includes('Jun') || 
            dateStr.includes('Jul') || dateStr.includes('Aug') || dateStr.includes('Sep') || 
            dateStr.includes('Oct') || dateStr.includes('Nov') || dateStr.includes('Dec'))) {
          
          // Remove day name and parse the date part
          const cleanDateStr = dateStr.replace(/^[A-Za-z]+,\s*/, ''); // Remove "Tue, "
          logDebug(`Cleaned date string: ${cleanDateStr}`);
          
          // Try parsing the cleaned date with time
          startDate = new Date(`${cleanDateStr} ${timeStr}`);
          
          if (isNaN(startDate.getTime())) {
            // Manual parsing for "Dec 2, 2025" format
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                              'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const dateMatch = cleanDateStr.match(/([A-Za-z]+)\s+(\d{1,2}),\s+(\d{4})/);
            
            if (dateMatch) {
              const monthName = dateMatch[1];
              const day = parseInt(dateMatch[2]);
              const year = parseInt(dateMatch[3]);
              const monthIndex = monthNames.indexOf(monthName);
              
              if (monthIndex !== -1) {
                // Parse time
                const timeParts = timeStr.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?/i);
                
                if (timeParts) {
                  let hours = parseInt(timeParts[1]);
                  const minutes = parseInt(timeParts[2]);
                  const seconds = parseInt(timeParts[3] || '0');
                  const ampm = timeParts[4];
                  
                  if (ampm) {
                    if (ampm.toUpperCase() === 'PM' && hours !== 12) {
                      hours += 12;
                    } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
                      hours = 0;
                    }
                  }
                  
                  startDate = new Date(year, monthIndex, day, hours, minutes, seconds);
                  logDebug(`Manually parsed date: ${startDate.toISOString()}`);
                } else {
                  // Default to noon if time parsing fails
                  startDate = new Date(year, monthIndex, day, 12, 0, 0);
                }
              }
            }
          }
        } else {
          // Handle other date formats with dashes or slashes
          const normalizedDateStr = dateStr.replace(/\//g, '-');
          const dateParts = normalizedDateStr.split('-');
          let year, month, day;
          
          if (dateParts.length === 3) {
            // Determine if it's YYYY-MM-DD or MM-DD-YYYY or DD-MM-YYYY
            if (dateParts[0].length === 4) {
              [year, month, day] = dateParts.map(Number);
            } else if (dateParts[2].length === 4) {
              [month, day, year] = dateParts.map(Number);
            } else {
              [day, month, year] = dateParts.map(Number);
              if (year < 100) year += 2000; // Handle 2-digit years
            }
            
            const timeParts = timeStr.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?/i);
            
            if (timeParts) {
              let hours = parseInt(timeParts[1]);
              const minutes = parseInt(timeParts[2]);
              const seconds = parseInt(timeParts[3] || '0');
              const ampm = timeParts[4];
              
              if (ampm) {
                if (ampm.toUpperCase() === 'PM' && hours !== 12) {
                  hours += 12;
                } else if (ampm.toUpperCase() === 'AM' && hours === 12) {
                  hours = 0;
                }
              }
              
              startDate = new Date(year, month - 1, day, hours, minutes, seconds);
            }
          }
        }
      }
      
      // Default session duration is 1 hour
      endDate = new Date(startDate.getTime() + (60 * 60 * 1000));
      
    } catch (error) {
      logError("Error parsing date/time", error instanceof Error ? error : new Error(String(error)));
      startDate = new Date();
      endDate = new Date(startDate.getTime() + (60 * 60 * 1000));
    }
  } else {
    // No valid date/time information found - try to extract from any available fields
    logWarn("No standard date/time fields found, checking all session properties");
    
    // Check all possible date fields in the session object
    const possibleDateFields = ['scheduledAt', 'timestamp', 'createdAt', 'dateTime'];
    let foundDate = false;
    
    for (const field of possibleDateFields) {
      if (session[field]) {
        try {
          if (session[field] instanceof Date) {
            startDate = session[field];
          } else if (session[field]?.toDate) {
            startDate = session[field].toDate();
          } else if (session[field]?.seconds) {
            startDate = new Date(session[field].seconds * 1000);
          } else {
            startDate = new Date(session[field]);
          }
          
          if (!isNaN(startDate.getTime())) {
            logDebug(`Found valid date in field '${field}': ${startDate.toISOString()}`);
            foundDate = true;
            break;
          }
        } catch (e) {
          logWarn(`Failed to parse date from field '${field}'`);
        }
      }
    }
    
    if (!foundDate) {
      logError("No valid date/time information found in session, using current time", new Error("Invalid session date"));
      startDate = new Date();
    }
    
    endDate = new Date(startDate.getTime() + (60 * 60 * 1000));
  }
  
  logDebug(`Processed dates - startAt: ${startDate.toISOString()}, endAt: ${endDate.toISOString()}`);
  
  // Final validation with better error handling
  if (!startDate || isNaN(startDate.getTime())) {
    logError("Invalid startDate, using current time as fallback", new Error("Invalid start date"));
    startDate = new Date();
  }
  if (!endDate || isNaN(endDate.getTime())) {
    logError("Invalid endDate, calculating from startDate + 1 hour", new Error("Invalid end date"));
    endDate = new Date(startDate.getTime() + (60 * 60 * 1000));
  }
  
  // Use consistent formatting for Philippines timezone
  const sessionDate = startDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Asia/Manila' // Ensure Philippines timezone
  });
  
  const sessionTime = `${startDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Manila' // Ensure Philippines timezone
  })} - ${endDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Manila' // Ensure Philippines timezone
  })}`;

  logDebug(`Formatted session data - Date: ${sessionDate}, Time: ${sessionTime}`);

  return {
    sessionDate,
    sessionTime,
    sessionMode: session.mode || session.type,
    location: session.location,
    meetingLink: session.meetingLink,
  };
};

export type { SessionApprovedData, SessionDeclinedData, SessionBookedData, SessionCancelledData, SessionRescheduledData };
